import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, Tag } from 'lucide-react';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import Loading from '../components/common/Loading';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import Input from '../components/common/Input';
import { Category, CategoryFormData } from '../types';
import { categoryService } from '../services/categoryService';

const emptyForm: CategoryFormData = {
  nome: '',
  ativo: true,
};

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [processing, setProcessing] = useState(false);

  const [formData, setFormData] = useState<CategoryFormData>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (err: any) {
      setError('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setEditingCategory(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowFormModal(true);
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setFormData({ nome: category.nome, ativo: category.ativo });
    setFormErrors({});
    setShowFormModal(true);
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.nome.trim()) errors.nome = 'Nome é obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setProcessing(true);
    try {
      if (editingCategory) {
        await categoryService.update(editingCategory.id, formData);
        setSuccess('Categoria atualizada com sucesso');
      } else {
        await categoryService.create(formData);
        setSuccess('Categoria criada com sucesso');
      }
      await loadCategories();
      setShowFormModal(false);
      resetForm();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.nome?.[0] ||
          err.response?.data?.message ||
          'Erro ao salvar categoria'
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleActive = async (category: Category) => {
    try {
      await categoryService.toggleActive(category.id);
      await loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao alterar status da categoria');
    }
  };

  const handleRemove = async () => {
    if (!selectedCategory) return;

    setProcessing(true);
    try {
      await categoryService.delete(selectedCategory.id);
      setSuccess('Categoria removida com sucesso');
      await loadCategories();
      setShowRemoveModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao remover categoria');
      setShowRemoveModal(false);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Categorias de Parceiros</h1>
            <p className="text-gray-600 mt-2">
              Usadas para organizar os cards de filtro em "Meus Parceiros"
            </p>
          </div>
          <Button onClick={openCreateModal}>
            <Plus className="w-5 h-5 mr-2" />
            Nova Categoria
          </Button>
        </div>

        {error && (
          <Alert variant="error" onClose={() => setError('')} className="mb-6">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} className="mb-6">
            {success}
          </Alert>
        )}

        {categories.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma categoria cadastrada</h3>
            <p className="text-gray-600 mb-6">Crie a primeira categoria para organizar os parceiros</p>
            <Button onClick={openCreateModal}>
              <Plus className="w-5 h-5 mr-2" />
              Nova Categoria
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{category.nome}</h3>
                  <Badge variant={category.ativo ? 'success' : 'secondary'}>
                    {category.ativo ? 'Ativa' : 'Inativa'}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  {category.partners_count ?? 0} parceiro(s) vinculado(s)
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEditModal(category)} title="Editar">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => handleToggleActive(category)}
                    title={category.ativo ? 'Inativar' : 'Ativar'}
                  >
                    {category.ativo ? (
                      <ToggleLeft className="w-4 h-4 mr-1" />
                    ) : (
                      <ToggleRight className="w-4 h-4 mr-1" />
                    )}
                    {category.ativo ? 'Inativar' : 'Ativar'}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      setSelectedCategory(category);
                      setShowRemoveModal(true);
                    }}
                    title="Remover"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal Criar/Editar */}
        {showFormModal && (
          <Modal
            isOpen={showFormModal}
            onClose={() => {
              setShowFormModal(false);
              resetForm();
            }}
            title={editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
          >
            <div className="space-y-4">
              <Input
                label="Nome"
                name="nome"
                value={formData.nome}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, nome: e.target.value }));
                  if (formErrors.nome) setFormErrors((prev) => ({ ...prev, nome: '' }));
                }}
                error={formErrors.nome}
                placeholder="Ex: Exames, Consultas, Farmácias..."
                required
              />

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.ativo}
                  onChange={(e) => setFormData((prev) => ({ ...prev, ativo: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Categoria ativa (aparece como card de filtro para associados)
              </label>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowFormModal(false);
                    resetForm();
                  }}
                  className="flex-1"
                  disabled={processing}
                >
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} loading={processing} disabled={processing} className="flex-1">
                  {editingCategory ? 'Salvar Alterações' : 'Criar Categoria'}
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal Remover */}
        {showRemoveModal && selectedCategory && (
          <Modal isOpen={showRemoveModal} onClose={() => setShowRemoveModal(false)} title="Remover Categoria">
            <div className="space-y-4">
              <p className="text-gray-600">
                Tem certeza que deseja remover a categoria <strong>{selectedCategory.nome}</strong>?
              </p>
              {(selectedCategory.partners_count ?? 0) > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    Esta categoria tem {selectedCategory.partners_count} parceiro(s) vinculado(s) — a remoção será
                    bloqueada até que sejam desvinculados.
                  </p>
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowRemoveModal(false)}
                  className="flex-1"
                  disabled={processing}
                >
                  Cancelar
                </Button>
                <Button
                  variant="danger"
                  onClick={handleRemove}
                  loading={processing}
                  disabled={processing}
                  className="flex-1"
                >
                  Confirmar Remoção
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default Categories;
